import { FrontendCoupon, FrontendStore } from '../../core/api/api.models';
import { CouponCardView } from './coupon-card.models';

/**
 * Builds the shared coupon-card view model from an API coupon.
 *
 * Extracted from the store page so every surface that renders the card produces the identical
 * shape — the discount pages call this too. Pure: the caller supplies the asset base URL rather
 * than the function reaching for an injector.
 */
export interface CouponCardMapOptions {
  /** Origin used to resolve relative `/uploads/...` paths. */
  assetBaseUrl: string;
  /** Title used when the coupon has no usable name. */
  fallbackTitle: string;
  /** Store name used when the coupon carries no store of its own — the page's own store. */
  fallbackStoreName: string;
  /** Expired coupons read "Expired:" instead of "Expires:". */
  expired?: boolean;
  /** Only the first card in a list can be the top pick. */
  index?: number;
}

function cleanText(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function toAssetUrl(path: string, assetBaseUrl: string): string {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${assetBaseUrl.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * How wide the big left-hand label is, in "average characters", for the CSS that sizes it.
 *
 * The size itself is worked out in CSS, not here: the discount column is 145px wide on a large
 * screen but 128px below 1500 and 112px below 1200, and a single value computed in TypeScript can
 * only ever suit one of them — which is why "2 FOR €30 LINGERIE" fitted on a wide screen and spilled
 * past the dashed rule on a laptop. The stylesheet knows the column width at each breakpoint, so it
 * only needs this one number from us.
 *
 * Spaces count for less than a letter because they are narrower, but they are counted: measuring
 * "2 FOR €30" as 7 characters instead of 9 was most of the overflow on its own.
 */
function discountBadgeChars(value: string): number {
  const letters = value.replace(/\s+/g, '').length;
  const spaces = value.trim().length - letters;
  return letters + spaces * 0.35;
}

/** Same idea for the small pill above the title. */
function pillBadgeFontSize(value: string): string | null {
  const length = value.replace(/\s+/g, '').length;
  if (length <= 8) return null;
  return `${Math.max(11, Math.min(15, 92 / length)).toFixed(1)}px`;
}

function expiryText(coupon: FrontendCoupon, expired: boolean): string {
  const prefix = expired ? 'Expired' : 'Expires';

  if (coupon.endDateText?.trim()) return `${prefix}: ${coupon.endDateText.trim()}`;

  if (coupon.endDate) {
    return `${prefix}: ${new Intl.DateTimeFormat('en', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(new Date(coupon.endDate))}`;
  }

  return 'Limited Time Offer';
}

function initials(value: string): string {
  const words = value
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);
  if (words.length === 0) return 'SA';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** True when the coupon's end date is in the past (end of that day). */
export function isCouponExpired(coupon: FrontendCoupon): boolean {
  if (!coupon.endDate) return false;

  const endDate = new Date(coupon.endDate);
  if (Number.isNaN(endDate.getTime())) return false;

  endDate.setHours(23, 59, 59, 999);
  return endDate.getTime() < Date.now();
}

export function toCouponCardView(
  coupon: FrontendCoupon,
  options: CouponCardMapOptions,
): CouponCardView {
  const { assetBaseUrl, fallbackTitle, fallbackStoreName, expired = false, index = 0 } = options;

  const badges = (coupon.couponBadges ?? [])
    .filter((badge): badge is string => typeof badge === 'string' && badge.trim().length > 0)
    .map((badge) => badge.trim());

  const code = cleanText(coupon.couponCode ?? '');
  const imageUrl =
    coupon.useCouponImage && coupon.couponImagePath
      ? toAssetUrl(coupon.couponImagePath, assetBaseUrl)
      : '';

  // Prefer the affiliate store (the actual brand the deal is for) over the page's store.
  const couponStore: FrontendStore | null = coupon.affiliateStore ?? coupon.store ?? null;
  const affiliateStore = coupon.affiliateStore ?? null;

  return {
    id: coupon.id,
    title: cleanText(coupon.name) || fallbackTitle,
    description: cleanText(coupon.detail ?? ''),
    typeLabel: badges[2] || (coupon.isDeal && !code ? 'Deal' : 'Code'),
    typeLabelFontSize: pillBadgeFontSize(badges[2] || (coupon.isDeal && !code ? 'Deal' : 'Code')),
    offerPrimary: badges[0] || (coupon.isDeal ? 'Deal' : 'Code'),
    offerPrimaryChars: discountBadgeChars(badges[0] || (coupon.isDeal ? 'Deal' : 'Code')),
    offerSecondary: badges[1] || (coupon.isDeal ? '' : 'CODE'),
    offerSecondaryChars: discountBadgeChars(badges[1] || (coupon.isDeal ? '' : 'CODE')),
    code,
    actionLabel: code ? 'Show Code' : 'Get Deal',
    expiryText: expiryText(coupon, expired),
    imageUrl,
    isTopPick: index === 0,
    isVerified: Boolean(coupon['isVerified']),
    freeShipping: Boolean(coupon['freeShipping']),
    studentDiscount: Boolean(coupon['studentDiscount']),
    badges: (coupon.badges ?? [])
      .filter((badge) => badge && badge.systemKey !== 'isExclusive')
      .map((badge) => ({
        label: String(badge.label || badge.name || '').trim(),
        color: String(badge.color || '#741FA2'),
        iconUrl: badge.iconImagePath ? toAssetUrl(String(badge.iconImagePath), assetBaseUrl) : '',
        systemKey: String(badge.systemKey || ''),
      })),
    storeName: couponStore?.name?.trim() || fallbackStoreName,
    storeLogoUrl: couponStore?.logoImagePath
      ? toAssetUrl(couponStore.logoImagePath, assetBaseUrl)
      : '',
    storeInitials: initials(couponStore?.name || fallbackStoreName),
    isExclusive: Boolean(coupon.isExclusive),
    affiliateStoreLogoUrl: affiliateStore?.logoImagePath
      ? toAssetUrl(affiliateStore.logoImagePath, assetBaseUrl)
      : '',
    affiliateStoreName: affiliateStore?.name?.trim() || '',
  };
}
